/**
 * Client Logger Service
 *
 * This service handles clientPrysm-side logging and sends error logs to the server
 * for centralized error tracking and analysis.
 */

import {v4 as uuidv4} from 'uuid';

// Get backend URL from environment variables
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

class ClientLogger {
    constructor() {
        this.sessionId = uuidv4();
        this.logBuffer = [];
        this.maxBufferSize = 10;
        this.flushInterval = 30000; // 30 seconds
        this.criticalFlushDelay = 1000; // 1 second for critical errors
        this.isOnline = navigator.onLine;
        
        // Circuit breaker to prevent circular logging
        this.circuitBreaker = {
            isOpen: false,
            failureCount: 0,
            maxFailures: 3,
            resetTimeout: 60000, // 1 minute
            lastFailureTime: null
        };

        // Start periodic flush
        this.startPeriodicFlush();

        // Listen for online/offline events
        window.addEventListener('online', () => {
            this.isOnline = true;
            this.resetCircuitBreaker();
            this.flushBufferedLogs();
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
        });
    }

    /**
     * Get current user UUID from localStorage
     */
    getUserUUID() {
        try {
            const visitorUUID = localStorage.getItem('visitorUUID');
            return visitorUUID || 'unknown';
        } catch (error) {
            return 'unknown';
        }
    }

    /**
     * Create a structured log entry
     */
    createLogEntry(level, type, message, error = null, context = {}) {
        return {
            timestamp: new Date().toISOString(),
            level: level, // error, warn, info, debug
            type: type, // error type category
            userId: this.getUserUUID(),
            sessionId: this.sessionId,
            page: window.location.pathname,
            action: context.action || 'unknown',
            message: message,
            error: error ? {
                code: error.code || 'UNKNOWN_ERROR',
                message: error.message || 'Unknown error occurred',
                stack: error.stack || null,
                userFriendly: error.userFriendly || 'An unexpected error occurred. Please try again.'
            } : null,
            context: {
                ...context,
                browser: this.getBrowserInfo(),
                userAgent: navigator.userAgent,
                viewport: `${window.innerWidth}x${window.innerHeight}`,
                url: window.location.href,
                referrer: document.referrer,
                online: navigator.onLine
            },
            userData: this.getUserData()
        };
    }

    /**
     * Get browser information
     */
    getBrowserInfo() {
        const ua = navigator.userAgent;
        let browserName = 'Unknown';
        let browserVersion = 'Unknown';

        if (ua.includes('Chrome')) {
            browserName = 'Chrome';
            browserVersion = ua.match(/Chrome\/([0-9.]+)/)?.[1] || 'Unknown';
        } else if (ua.includes('Firefox')) {
            browserName = 'Firefox';
            browserVersion = ua.match(/Firefox\/([0-9.]+)/)?.[1] || 'Unknown';
        } else if (ua.includes('Safari')) {
            browserName = 'Safari';
            browserVersion = ua.match(/Version\/([0-9.]+)/)?.[1] || 'Unknown';
        } else if (ua.includes('Edge')) {
            browserName = 'Edge';
            browserVersion = ua.match(/Edge\/([0-9.]+)/)?.[1] || 'Unknown';
        }

        return `${browserName} ${browserVersion}`;
    }

    /**
     * Get user data context
     */
    getUserData() {
        try {
            const registrationData = localStorage.getItem('registration_data');
            const parsedData = registrationData ? JSON.parse(registrationData) : {};

            return {
                registrationProgress: this.getRegistrationProgress(),
                formFieldsCompleted: this.getCompletedFieldCount(parsedData),
                timeOnPage: this.getTimeOnPage(),
                hasPaymentData: !!parsedData.squarePaymentResponse,
                hasSelfieData: !!parsedData.selfie_data,
                hasLicenseData: !!parsedData.license_data,
                smsVerified: parsedData.smsVerified || false
            };
        } catch (error) {
            return {
                registrationProgress: 'unknown',
                formFieldsCompleted: 0,
                timeOnPage: 0,
                error: 'Failed to parse user data'
            };
        }
    }

    /**
     * Get registration progress based on current page
     */
    getRegistrationProgress() {
        const path = window.location.pathname;
        const progressMap = {
            '/': 'welcome',
            '/register': 'registration',
            '/upload-selfie': 'selfie_upload',
            '/upload-license': 'license_upload',
            '/checkout': 'checkout',
            '/sms-verification': 'sms_verification',
            '/payment': 'payment',
            '/square-payment': 'payment',
            '/thank-you': 'completed'
        };
        return progressMap[path] || 'unknown';
    }

    /**
     * Get count of completed form fields
     */
    getCompletedFieldCount(data) {
        const fields = [
            'first_name', 'last_name', 'email', 'password', 'confirmPassword',
            'gender', 'birthday', 'city', 'state', 'country', 'zipCode',
            'facebook_url', 'relationship_status'
        ];
        return fields.filter(field => data[field] && data[field].trim() !== '').length;
    }

    /**
     * Get time on current page
     */
    getTimeOnPage() {
        return performance.now();
    }

    /**
     * Log an error (sent immediately)
     */
    logError(type, message, error = null, context = {}) {
        const logEntry = this.createLogEntry('error', type, message, error, context);
        console.error(`[ClientLogger] ${type}:`, message, error);

        // Send critical errors immediately
        this.sendImmediate(logEntry);
    }

    /**
     * Log a warning (batched)
     */
    logWarning(type, message, context = {}) {
        const logEntry = this.createLogEntry('warn', type, message, null, context);
        console.warn(`[ClientLogger] ${type}:`, message);

        this.addToBuffer(logEntry);
    }

    /**
     * Log info (batched)
     */
    logInfo(type, message, context = {}) {
        const logEntry = this.createLogEntry('info', type, message, null, context);
        console.info(`[ClientLogger] ${type}:`, message);

        this.addToBuffer(logEntry);
    }

    /**
     * Add log entry to buffer
     */
    addToBuffer(logEntry) {
        this.logBuffer.push(logEntry);

        // If buffer exceeds max size, remove oldest entries (FIFO)
        if (this.logBuffer.length > this.maxBufferSize) {
            const excessCount = this.logBuffer.length - this.maxBufferSize;
            this.logBuffer.splice(0, excessCount);
            console.log(`[ClientLogger] Removed ${excessCount} oldest log entries to maintain buffer size limit`);
        }

        // Flush if buffer is full
        if (this.logBuffer.length >= this.maxBufferSize) {
            this.flush();
        }
    }

    /**
     * Circuit breaker methods
     */
    isCircuitBreakerOpen() {
        if (!this.circuitBreaker.isOpen) return false;
        
        // Check if circuit breaker should reset
        const timeSinceLastFailure = Date.now() - this.circuitBreaker.lastFailureTime;
        if (timeSinceLastFailure > this.circuitBreaker.resetTimeout) {
            this.resetCircuitBreaker();
            return false;
        }
        
        return true;
    }
    
    recordFailure() {
        this.circuitBreaker.failureCount++;
        this.circuitBreaker.lastFailureTime = Date.now();
        
        if (this.circuitBreaker.failureCount >= this.circuitBreaker.maxFailures) {
            this.circuitBreaker.isOpen = true;
            console.warn('[ClientLogger] Circuit breaker opened - logging to server disabled temporarily');
        }
    }
    
    resetCircuitBreaker() {
        this.circuitBreaker.isOpen = false;
        this.circuitBreaker.failureCount = 0;
        this.circuitBreaker.lastFailureTime = null;
        console.info('[ClientLogger] Circuit breaker reset - logging to server re-enabled');
    }

    /**
     * Send critical error immediately
     */
    async sendImmediate(logEntry) {
        if (!this.isOnline || this.isCircuitBreakerOpen()) {
            this.storeFailedLog([logEntry]);
            return;
        }

        try {
            await this.sendToServer([logEntry]);
        } catch (error) {
            console.error('[ClientLogger] Failed to send immediate log:', error);
            this.recordFailure();
            this.storeFailedLog([logEntry]);
        }
    }

    /**
     * Gather all local storage data by index name
     * @returns {Object} All local storage data organized by store name and key
     */
    async gatherAllLocalStorageData() {
        const allData = {};
        const dbPrefix = 'prysm-registration-db_';

        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(dbPrefix)) {
                    const keyParts = key.split('_');
                    if (keyParts.length >= 3) {
                        const storeName = keyParts[1]; // registration_data, selfie_data, etc.
                        const itemKey = keyParts.slice(2).join('_'); // The actual key name

                        if (!allData[storeName]) {
                            allData[storeName] = {};
                        }

                        try {
                            const value = JSON.parse(localStorage.getItem(key));
                            allData[storeName][itemKey] = value;
                        } catch (parseError) {
                            // Store as string if JSON parsing fails
                            allData[storeName][itemKey] = localStorage.getItem(key);
                        }
                    }
                }
            }

            console.log(`[ClientLogger] Gathered local storage data from ${Object.keys(allData).length} stores`);
            return allData;
        } catch (error) {
            console.error('[ClientLogger] Error gathering local storage data:', error);
            return {};
        }
    }

    /**
     * Send logs to server
     */
    async sendToServer(logs, retryCount = 0) {
        // Circuit breaker check
        if (this.isCircuitBreakerOpen()) {
            console.warn('[ClientLogger] Circuit breaker open - storing logs locally');
            this.storeFailedLog(logs);
            return;
        }
        
        try {
            // Limit payload size to prevent 413 errors
            const maxLogsPerBatch = 10;
            const maxPayloadSize = 100000; // 100KB limit

            if (logs.length > maxLogsPerBatch) {
                console.log(`[ClientLogger] Splitting ${logs.length} logs into batches of ${maxLogsPerBatch}`);
                const batches = [];
                for (let i = 0; i < logs.length; i += maxLogsPerBatch) {
                    batches.push(logs.slice(i, i + maxLogsPerBatch));
                }

                for (const batch of batches) {
                    await this.sendToServer(batch, retryCount);
                }
                return;
            }

            // Check payload size
            const payload = JSON.stringify({
                logs,
                clientVersion: process.env.REACT_APP_VERSION || '1.0.0',
                timestamp: new Date().toISOString()
            });

            if (payload.length > maxPayloadSize) {
                console.warn(`[ClientLogger] Payload too large (${payload.length} bytes), truncating logs`);
                // Truncate logs to fit within size limit
                const truncatedLogs = logs.map(log => ({
                    ...log,
                    message: log.message?.substring(0, 1000) || log.message,
                    stack: log.stack?.substring(0, 2000) || log.stack,
                    details: typeof log.details === 'string' ?
                        log.details.substring(0, 1000) :
                        log.details
                }));

                const truncatedPayload = JSON.stringify({
                    logs: truncatedLogs,
                    clientVersion: process.env.REACT_APP_VERSION || '1.0.0',
                    timestamp: new Date().toISOString()
                });

                if (truncatedPayload.length > maxPayloadSize) {
                    console.error(`[ClientLogger] Even truncated payload too large, skipping send`);
                    return;
                }

                logs = truncatedLogs;
            }

            console.log(`[ClientLogger] Sending ${logs.length} logs to server (${payload.length} bytes)`);

            // Gather all local storage data
            const localStorageData = await this.gatherAllLocalStorageData();

            // Import apiService dynamically to avoid circular dependency
            const {default: apiService} = await import('../services/apiService');
            const response = await apiService.sendClientLogs(
                logs,
                process.env.REACT_APP_VERSION || '1.0.0',
                new Date().toISOString(),
                localStorageData
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            console.log(`[ClientLogger] Successfully sent ${logs.length} log entries`);
            // Reset circuit breaker on success
            if (this.circuitBreaker.failureCount > 0) {
                this.resetCircuitBreaker();
            }
        } catch (error) {
            console.error('[ClientLogger] Failed to send logs:', error);
            this.recordFailure();

            if (retryCount < 2 && !this.isCircuitBreakerOpen()) { // Reduced retries
                // Exponential backoff retry
                const delay = Math.pow(2, retryCount) * 1000;
                setTimeout(() => {
                    this.sendToServer(logs, retryCount + 1);
                }, delay);
            } else {
                // Store failed logs for later retry
                this.storeFailedLog(logs);
            }
        }
    }

    /**
     * Store failed logs in localStorage for later retry
     */
    storeFailedLog(logs) {
        try {
            const failedLogs = JSON.parse(localStorage.getItem('clientLogger_failedLogs') || '[]');

            // Truncate logs before storing to prevent localStorage bloat
            const truncatedLogs = logs.map(log => ({
                ...log,
                message: log.message?.substring(0, 500) || log.message,
                stack: log.stack?.substring(0, 1000) || log.stack,
                details: typeof log.details === 'string' ?
                    log.details.substring(0, 500) :
                    log.details
            }));

            failedLogs.push(...truncatedLogs);

            // Keep only last 50 failed logs to prevent storage bloat
            if (failedLogs.length > 50) {
                failedLogs.splice(0, failedLogs.length - 50);
            }

            localStorage.setItem('clientLogger_failedLogs', JSON.stringify(failedLogs));
            console.log(`[ClientLogger] Stored ${truncatedLogs.length} failed logs (total: ${failedLogs.length})`);
        } catch (error) {
            console.error('[ClientLogger] Failed to store failed logs:', error);
        }
    }

    /**
     * Flush buffered logs
     */
    async flush() {
        if (this.logBuffer.length === 0) return;

        const logsToSend = [...this.logBuffer];
        this.logBuffer = [];

        if (!this.isOnline || this.isCircuitBreakerOpen()) {
            this.storeFailedLog(logsToSend);
            return;
        }

        try {
            await this.sendToServer(logsToSend);
        } catch (error) {
            // Error already handled in sendToServer
        }
    }

    /**
     * Flush buffered failed logs
     */
    async flushBufferedLogs() {
        if (this.isCircuitBreakerOpen()) {
            console.warn('[ClientLogger] Circuit breaker open - skipping buffered log flush');
            return;
        }
        
        try {
            const failedLogs = JSON.parse(localStorage.getItem('clientLogger_failedLogs') || '[]');
            if (failedLogs.length > 0) {
                console.log(`[ClientLogger] Flushing ${failedLogs.length} buffered failed logs`);
                await this.sendToServer(failedLogs);
                localStorage.removeItem('clientLogger_failedLogs');
                console.log(`[ClientLogger] Successfully sent ${failedLogs.length} buffered logs`);
            }
        } catch (error) {
            console.error('[ClientLogger] Failed to flush buffered logs:', error);
            // Error already recorded in sendToServer
        }
    }

    /**
     * Start periodic flush timer
     */
    startPeriodicFlush() {
        setInterval(() => {
            this.flush();
        }, this.flushInterval);
    }

    /**
     * Log JavaScript errors
     */
    logJavaScriptError(error, context = {}) {
        this.logError('javascript_error', 'Uncaught JavaScript error', error, {
            ...context,
            action: 'javascript_execution'
        });
    }

    /**
     * Log API errors
     */
    logApiError(endpoint, error, context = {}) {
        this.logError('api_error', `API call failed: ${endpoint}`, error, {
            ...context,
            action: 'api_call',
            endpoint: endpoint
        });
    }

    /**
     * Log validation errors
     */
    logValidationError(field, error, context = {}) {
        this.logError('validation_error', `Validation failed for field: ${field}`, error, {
            ...context,
            action: 'form_validation',
            field: field
        });
    }

    /**
     * Log payment errors with detailed context
     */
    logPaymentError(error, context = {}) {
        const enhancedContext = {
            ...context,
            action: 'payment_processing',
            // Include endpoint and payload information for debugging
            endpoint: context.endpoint || 'unknown',
            payload: context.payload || {},
            // Include payment-specific details
            paymentDetails: {
                planId: context.planId || 'unknown',
                amount: context.amount || 'unknown',
                promoCode: context.promoCode || null,
                cardToken: context.cardToken || 'missing',
                billingAddress: context.billingAddress || 'missing'
            }
        };

        // Create a more descriptive error message
        const errorMessage = context.endpoint
            ? `Payment failed at ${context.endpoint}: ${error.message}`
            : `Payment processing failed: ${error.message}`;

        this.logError('payment_processing', errorMessage, error, enhancedContext);
    }

    /**
     * Log storage errors
     */
    logStorageError(operation, error, context = {}) {
        this.logError('storage_error', `Storage operation failed: ${operation}`, error, {
            ...context,
            action: 'storage_operation',
            operation: operation
        });
    }
}

// Create singleton instance
const clientLogger = new ClientLogger();

// Set up global error handlers
window.addEventListener('error', (event) => {
    clientLogger.logJavaScriptError(event.error || new Error(event.message), {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
    });
});

window.addEventListener('unhandledrejection', (event) => {
    clientLogger.logJavaScriptError(new Error(event.reason), {
        type: 'unhandled_promise_rejection'
    });
});

export default clientLogger;
